import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, StatusBadge, DataTable } from '@etherisc/ui-kit';
import { 
  ChirpsJobOutput, 
  ChirpsDownloadedFile, 
  ChirpsFailedFile 
} from '../../types/job';
import { 
  Download, 
  Database, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  FileText,
  Globe
} from 'lucide-react';

interface ChirpsJobOutputVisualizationProps {
  output: ChirpsJobOutput;
}

export const ChirpsJobOutputVisualization: React.FC<ChirpsJobOutputVisualizationProps> = ({ output }) => {
  // Helper function to safely display values
  const safeDisplay = (value: unknown): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return String(value);
  };

  // Format bytes to MB
  const formatMB = (mb: number): string => {
    if (mb === null || mb === undefined) return 'N/A';
    return `${mb.toFixed(2)} MB`;
  };

  // Format percentage
  const formatPercent = (percent: number): string => {
    if (percent === null || percent === undefined) return 'N/A';
    return `${percent.toFixed(1)}%`;
  };

  // Format duration
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null || seconds === undefined) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Get success rate variant
  const getSuccessRateVariant = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 80) return 'success';
    if (rate >= 50) return 'warning';
    return 'error';
  };

  // Columns for downloaded files table
  const downloadedFilesColumns = [
    {
      id: 'filename',
      header: 'Filename',
      accessorKey: 'filename',
      cell: ({ row }: { row: { original: ChirpsDownloadedFile } }) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-green-500" />
          <span className="font-mono text-sm">{row.original.filename}</span>
        </div>
      ),
    },
    {
      id: 'size_mb',
      header: 'Size',
      accessorKey: 'size_mb',
      cell: ({ row }: { row: { original: ChirpsDownloadedFile } }) => (
        <span className="text-sm">{formatMB(row.original.size_mb)}</span>
      ),
    },
    {
      id: 'checksum',
      header: 'Checksum',
      accessorKey: 'checksum',
      cell: ({ row }: { row: { original: ChirpsDownloadedFile } }) => (
        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
          {safeDisplay(row.original.checksum)}
        </code>
      ),
    },
  ];

  // Columns for failed files table
  const failedFilesColumns = [
    {
      id: 'filename',
      header: 'Filename',
      accessorKey: 'filename',
      cell: ({ row }: { row: { original: ChirpsFailedFile } }) => (
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-500" />
          <span className="font-mono text-sm">{row.original.filename}</span>
        </div>
      ),
    },
    {
      id: 'error',
      header: 'Error',
      accessorKey: 'error',
      cell: ({ row }: { row: { original: ChirpsFailedFile } }) => (
        <div className="text-sm text-red-600 max-w-xs">
          {safeDisplay(row.original.error)}
        </div>
      ),
    },
    {
      id: 'url',
      header: 'URL',
      accessorKey: 'url',
      cell: ({ row }: { row: { original: ChirpsFailedFile } }) => (
        <a 
          href={row.original.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 text-sm truncate block max-w-xs"
        >
          {row.original.url}
        </a>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Job Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {safeDisplay(output.summary?.total_files_processed)}
              </div>
              <div className="text-sm text-gray-600">Total Processed</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {safeDisplay(output.summary?.newly_downloaded)}
              </div>
              <div className="text-sm text-gray-600">Downloaded</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {safeDisplay(output.summary?.already_downloaded_duplicates)}
              </div>
              <div className="text-sm text-gray-600">Duplicates</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {safeDisplay(output.summary?.failed)}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Success Rate:</span>
              <StatusBadge variant={getSuccessRateVariant(output.summary?.success_rate_percent || 0)}>
                {formatPercent(output.summary?.success_rate_percent || 0)}
              </StatusBadge>
            </div>
            {output.summary?.error_threshold_exceeded && (
              <div className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Error threshold exceeded</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Download Metrics & Data Specification */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              Download Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Total Downloaded:</span>
              <span className="font-medium">{formatMB(output.download_metrics?.total_mb_downloaded || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Average File Size:</span>
              <span className="font-medium">{formatMB(output.download_metrics?.avg_file_size_mb || 0)}</span>
            </div>
            <div className="space-y-1">
              <span className="text-sm text-gray-600">Download URL:</span>
              <a 
                href={output.download_metrics?.download_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm break-all block"
              >
                {safeDisplay(output.download_metrics?.download_url)}
              </a>
            </div>
            <div className="space-y-1">
              <span className="text-sm text-gray-600">Storage Path:</span>
              <code className="text-sm bg-gray-100 px-2 py-1 rounded block break-all">
                {safeDisplay(output.download_metrics?.storage_path)}
              </code>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-600" />
              Data Specification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Data Source:</span>
              <span className="font-medium">{safeDisplay(output.data_specification?.data_source)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Resolution:</span>
              <span className="font-medium">{safeDisplay(output.data_specification?.temporal_resolution)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Region:</span>
              <span className="font-medium">{safeDisplay(output.data_specification?.region)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Format:</span>
              <span className="font-medium">{safeDisplay(output.data_specification?.format)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Quality:</span>
              <span className="font-medium">{safeDisplay(output.data_specification?.quality)}</span>
            </div>
            <div className="space-y-1">
              <span className="text-sm text-gray-600">Years Requested:</span>
              <div className="flex flex-wrap gap-1">
                {Array.isArray(output.data_specification?.years_requested) && output.data_specification.years_requested.length > 0 ? (
                  output.data_specification.years_requested.map((year) => (
                    <StatusBadge key={year} variant="neutral">{year}</StatusBadge>
                  ))
                ) : (
                  <span className="text-sm">{safeDisplay(output.data_specification?.years_requested)}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timing & Database Records */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              Timing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-gray-600">Started At:</span>
              <div className="font-medium">{formatTimestamp(output.timing?.job_started_at || '')}</div>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Duration:</span>
              <span className="font-medium">{formatDuration(output.timing?.processing_duration_seconds)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-indigo-600" />
              Database Records
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New Entries Created:</span>
              <span className="font-medium">{safeDisplay(output.database_records?.raw_data_entries_created)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Duplicates Skipped:</span>
              <span className="font-medium">{safeDisplay(output.database_records?.duplicate_files_skipped)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* File Details */}
      {(output.file_details?.newly_downloaded_files?.length > 0 || output.file_details?.failed_files?.length > 0) && (
        <div className="space-y-6">
          {/* Downloaded Files */}
          {output.file_details?.newly_downloaded_files?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Successfully Downloaded Files ({output.file_details.newly_downloaded_files.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={output.file_details.newly_downloaded_files}
                  columns={downloadedFilesColumns}
                  enableSorting={true}
                  enableResizing={false}
                />
              </CardContent>
            </Card>
          )}

          {/* Failed Files */}
          {output.file_details?.failed_files?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  Failed Downloads ({output.file_details.failed_files.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={output.file_details.failed_files}
                  columns={failedFilesColumns}
                  enableSorting={true}
                  enableResizing={false}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}; 